import React, {
  useState, useRef, useEffect, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'language/i18n';
import _ from 'lodash';

import { sendSmsCode, verifyPhone, updateRegisterStep } from 'models/user';
import { StatusContext } from 'contexts/statusContext';
import {
  TextInput, Form, Button, CallTo,
} from 'components/common';
import { USER_MEMBERSHIP_TYPE } from 'constants/common';

const styles = {
  codeInputBoxesContainer: {
    display: 'flex',
    justifyContent: 'center',
    columnGap: '10px',
  },
  codeInputBox: {
    fontSize: 40,
    fontWeight: '900',
    color: '#4d4e4e',
    textAlign: 'center',
  },
};

const CodeEnterence = ({
  t, userState, router, onSubmit = () => {},
}) => {
  const { setErrorState } = useContext(StatusContext);

  const [onResponse, setOnResponse] = useState(false);
  const [time, setTime] = useState(90);
  const [code, setCode] = useState(['', '', '', '', '', '']);

  let formRef = useRef();
  let code0 = useRef();
  let code1 = useRef();
  let code2 = useRef();
  let code3 = useRef();
  let code4 = useRef();
  let code5 = useRef();

  const inputChange = (name, value, line) => {
    if (line === 0) {
      if (value !== '') {
        code1.focus();
      }
    } else if (line === 1) {
      if (value === '') {
        code0.focus();
      } else {
        code2.focus();
      }
    } else if (line === 2) {
      if (value === '') {
        code1.focus();
      } else {
        code3.focus();
      }
    } else if (line === 3) {
      if (value === '') {
        code2.focus();
      } else {
        code4.focus();
      }
    } else if (line === 4) {
      if (value === '') {
        code3.focus();
      } else {
        code5.focus();
      }
    } else if (line === 5) {
      if (value === '') {
        code4.focus();
      } else {
        code5.blur();
      }
    }
    const newCode = code;
    newCode[line] = value;
    setCode(newCode);
  };

  const handleBackspaceDeletion = (e, line) => {
    const refs = [code0, code1, code2, code3, code4, code5];

    if (e.key === 'Backspace') {
      e.preventDefault();

      if (!code[line] && line > 0) {
        const newCode = [...code];
        newCode[line - 1] = '';
        setCode(newCode);
        refs[line - 1].focus();
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(time - 1);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [time]);

  const clear = (numberOfCode) => {
    const fields = {};
    const newCode = code;

    for (let i = numberOfCode; i < code.length; i++) {
      fields[`code${i}`] = '';
      newCode[i] = '';
    }

    formRef.setFieldsValue({
      ...fields,
    });
    setCode(newCode);
  };

  const onFormSubmit = async () => {
    const codeToString = _.map(code).join('');
    onSubmit(codeToString);

    if (!onResponse) {
      try {
        setOnResponse(true);
        await sendSmsCode({
          code: codeToString,
          phoneNumber: userState.phoneCode + userState.phone,
        });
        await updateRegisterStep();
        if (userState.membershipType === USER_MEMBERSHIP_TYPE.CORPORATE) {
          router.push({
            pathname: '/register/create-company',
            query: {
              id: userState.id,
            },
          });
        } else {
          router.push({
            pathname: '/register/register-success',
          });
        }
      } catch (error) {
        console.log('codeEnterenceErr', error);
        if (error.status) {
          setErrorState({
            title: t('alert'),
            text: error.message,
            buttonText: t('close'),
            onClickButton: () => setErrorState({}),
          });
        }
      } finally {
        setOnResponse(false);
      }
    }
  };

  const sendCodeAgain = async () => {
    try {
      await verifyPhone({
        userId: userState.id,
        phoneNumber: userState.phoneCode + userState.phone,
      });
      setErrorState({
        title: t('successful'),
        type: 'success',
        text: 'Onay kodunuz tekrar gönderildi.',
        buttonText: t('close'),
        onClickButton: () => setErrorState({}),
      });
      setTime(90);
    } catch (error) {
      console.log('verifyPhoneErr', error);
      if (error.status) {
        setErrorState({
          title: t('alert'),
          text: error.message,
          buttonText: t('close'),
          onClickButton: () => setErrorState({}),
        });
      }
    } finally {
      setOnResponse(false);
    }
  };

  return (
    <div className="col-md-10 col-lg-8 offset-lg-1 mt-5 mt-lg-0">
      <Form
        formRef={(ref) => {
          formRef = ref;
        }}
        onFinish={onFormSubmit}
      >

        <div className="fs-22 mb-2">{t('stepNumber', { step: 4 })}</div>
        <div className="fs-22 fw-900 mb-3">{t('verifyYourPhoneNumber')}</div>
        <div className="mb-4">
          Onay kodunuz
          {' '}
          <span className="fw-700">{`+${userState.phone.slice(0, 2)} (${userState.phone.slice(2, 5)}) ${userState.phone.slice(5, 8)} ${userState.phone.slice(8, 10)} ${userState.phone.slice(10)}`}</span>
          {' '}
          numaralı telefona iletilmiştir.
        </div>

        <div className="row mb-4">
          <div className="col">
            <div className="im-input-boxes" data-focus-to-first="true" style={styles.codeInputBoxesContainer}>
              <input className="im-input-boxes__input" type="hidden" autoComplete="off" name="Code" value="" />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code0"
                onChange={({ name, value }) => inputChange(name, value, 0)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 0)}
                value={code[0]}
                onFocus={() => clear(0)}
                inputRef={(ref) => {
                  code0 = ref;
                }}
              />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code1"
                onChange={({ name, value }) => inputChange(name, value, 1)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 1)}
                onFocus={() => clear(1)}
                value={code[1]}
                inputRef={(ref) => {
                  code1 = ref;
                }}
              />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code2"
                onChange={({ name, value }) => inputChange(name, value, 2)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 2)}
                onFocus={() => clear(2)}
                value={code[2]}
                inputRef={(ref) => {
                  code2 = ref;
                }}
              />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code3"
                onChange={({ name, value }) => inputChange(name, value, 3)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 3)}
                onFocus={() => clear(3)}
                value={code[3]}
                inputRef={(ref) => {
                  code3 = ref;
                }}
              />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code4"
                onChange={({ name, value }) => inputChange(name, value, 4)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 4)}
                onFocus={() => clear(4)}
                value={code[4]}
                inputRef={(ref) => {
                  code4 = ref;
                }}
              />
              <TextInput
                className="im-input-boxes__box"
                style={styles.codeInputBox}
                name="code5"
                onChange={({ name, value }) => inputChange(name, value, 5)}
                onKeyDown={(e) => handleBackspaceDeletion(e, 5)}
                onFocus={() => clear(5)}
                value={code[5]}
                inputRef={(ref) => {
                  code5 = ref;
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col">
            <Button
              loading={onResponse}
              htmlType="submit"
              className="im-button im-button--primary im-button--fluid im-button--center mt-3"
              label="Telefon Numaramı Doğrula"
            />
          </div>
        </div>

        <div className="row text-center mt-4">
          <div className="col">
            {time > 0
              ? <p className="fw-700">{t('waitForSendCode', { time })}</p>
              : (
                <a className="im-button im-button--blank" onClick={() => sendCodeAgain()}>
                  <i className="fa-solid fa-redo-alt mr-2" />
                  <span className="text-gray4">{t('sendNewCode')}</span>
                </a>
              ) }
          </div>
        </div>

        <div className="row mt-4">
          <div className="col">
            <p className="fw-700">{t('didntReceiveVerificationCode')}</p>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <p className="fw-400">
              Telefonunuzu doğrulamada sorun yaşıyorsanız,
              {' '}
              <CallTo style={{ fontWeight: '700', color: '#4d4e4e' }} tel="+90 (850) 885 46 25" />
              {' '}
              numaralı telefondan bize ulaşabilirsiniz.
            </p>
            <p className="fw-400">
              Eğer telefon numarası onaylanmadıysa, sisteme giriş yapmaya çalıştığınızda "Telefon numaranız onaylı değil." uyarısı gözükür. Telefon numarası onaylı olmayan kullanıcılar; teminat yatırmak, teklif vermek dahil olmak üzere pek çok sistemsel özellikten yararlanamazlar. Cep telefonunuzu mutlaka onaylatınız.
            </p>
          </div>
        </div>
      </Form>
    </div>
  );
};

CodeEnterence.propTypes = {
  onSubmit: PropTypes.func,
  t: PropTypes.func,
  router: PropTypes.object,
  userState: PropTypes.object,
};

export default withTranslation('common')(CodeEnterence);
